import { useMemo } from "react"
import fp from "lodash/fp"

import type { EntityType } from "types/common.types"

import { getGeohash } from "helpers/utils/common"
import useDevices from "./useDevices"
import useSelectedTowers from "./towers/useSelectedTowers"

export function useEntityGeohashes(
  entityIds: number[],
  entityType: EntityType,
  skip = false,
): string[] {
  const { devsWRelsById } = useDevices({ skip: skip || entityType !== "device" })
  const { towers } = useSelectedTowers({
    towerIds: entityIds,
    skip: skip || entityType !== "tower",
  })

  const geohashes = useMemo(() => {
    if (skip) return []

    if (entityType === "tower" && towers.length > 0) {
      return fp.compact(
        towers.map((tower) => {
          if (!tower?.geo_location?.latitude || !tower?.geo_location?.longitude)
            return null
          return getGeohash(tower.geo_location.latitude, tower.geo_location.longitude)
        }),
      )
    }

    if (entityType === "device" && devsWRelsById) {
      return fp.compact(
        entityIds.map((id) => {
          const deviceWRels = devsWRelsById[id]
          const geoLocation = deviceWRels?.device?.settings?.geo_location
          if (!geoLocation?.latitude || !geoLocation?.longitude) return null
          return getGeohash(geoLocation.latitude, geoLocation.longitude)
        }),
      )
    }

    return []
  }, [entityIds, entityType, towers, devsWRelsById, skip])

  return geohashes
}
